import React, { useMemo, useState } from 'react';
import { ChartConfig } from '../types';
import { cleanLabel } from '../utils/stringUtils';

interface HeatmapRendererProps {
    data: any[];
    config: ChartConfig;
    width?: number;
    height?: number;
    palette?: string[];
}

// Helper to parse hex to rgb
const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
};

// Interpolate between multiple colors
const interpolateColor = (value: number, min: number, max: number, palette: string[]) => {
    if (!palette || palette.length === 0) return '#ccc';
    if (palette.length === 1) return palette[0];

    const ratio = (value - min) / (max - min || 1);
    const clampedRatio = Math.max(0, Math.min(1, ratio));

    // Find which segment of the palette we are in
    const segmentCount = palette.length - 1;
    const segmentIndex = Math.floor(clampedRatio * segmentCount);
    const segmentRatio = (clampedRatio * segmentCount) - segmentIndex;

    // Handle edge case where ratio is exactly 1
    const index1 = Math.min(segmentIndex, segmentCount - 1);
    const index2 = index1 + 1;

    const c1 = hexToRgb(palette[index1]);
    const c2 = hexToRgb(palette[index2]);

    const r = Math.round(c1.r + (c2.r - c1.r) * segmentRatio);
    const g = Math.round(c1.g + (c2.g - c1.g) * segmentRatio);
    const b = Math.round(c1.b + (c2.b - c1.b) * segmentRatio);

    return `rgb(${r}, ${g}, ${b})`;
};

// WeaverBI gold-themed palettes
const HEATMAP_PALETTES = {
    weaverGold: ['#1a1a2e', '#3d3d5c', '#7a5c00', '#d4a017', '#f59e0b', '#fbbf24', '#fcd34d'],
    sequential: ['#1e3a5f', '#2a6f97', '#4ea8de', '#90e0ef', '#caf0f8'],
    diverging: ['#3b82f6', '#93c5fd', '#ffffff', '#fca5a5', '#ef4444'],
    viridis: ['#440154', '#3e4989', '#26828e', '#35b779', '#fde725']
};

export const HeatmapRenderer: React.FC<HeatmapRendererProps> = ({
    data,
    config,
    width = 800,
    height = 600,
    palette // Will use WeaverBI gold if not provided
}) => {
    const [hoveredCell, setHoveredCell] = useState<any | null>(null);

    // Use WeaverBI gold palette by default, or provided palette
    const effectivePalette = useMemo(() => {
        if (palette && palette.length > 0) return palette;
        // Check config for colorScale preference (scientific or standard)
        const sciStyle = config.advanced?.scientific?.style || config.scientificConfig?.style;
        const colorScale = sciStyle?.colormap || config.colorScale || 'weaverGold';
        return HEATMAP_PALETTES[colorScale as keyof typeof HEATMAP_PALETTES] || HEATMAP_PALETTES.weaverGold;
    }, [palette, config]);

    const { xCategories, yCategories, minValue, maxValue, processedData } = useMemo(() => {
        // Check if data is already pre-processed by dataService (has x, y, value keys)
        const isPreProcessed = data.length > 0 && data[0].x !== undefined && data[0].y !== undefined && data[0].value !== undefined;

        if (isPreProcessed) {
            console.log('[HeatmapRenderer] Using pre-processed data from dataService');

            // Data is already in correct format, just validate and pass through
            const mapped = data.map(d => ({
                x: d.x,
                y: d.y,
                value: typeof d.value === 'number' ? d.value : parseFloat(d.value) || 0,
                image: d.image, // Pass through image field for image labels
                original: d.original || d
            })).filter(d => d.x != null && d.y != null && !isNaN(d.value));

            if (mapped.length === 0) {
                return {
                    xCategories: [],
                    yCategories: [],
                    minValue: 0,
                    maxValue: 1,
                    processedData: [],
                    error: 'No valid data found in pre-processed data'
                };
            }

            const xCats = Array.from(new Set(mapped.map(d => d.x))).sort((a: any, b: any) => {
                const an = Number(a), bn = Number(b);
                if (!isNaN(an) && !isNaN(bn)) return an - bn;
                return String(a).localeCompare(String(b));
            });
            const yCats = Array.from(new Set(mapped.map(d => d.y))).sort((a: any, b: any) => {
                const an = Number(a), bn = Number(b);
                if (!isNaN(an) && !isNaN(bn)) return an - bn;
                return String(a).localeCompare(String(b));
            });

            const values = mapped.map(d => d.value);
            return {
                xCategories: xCats,
                yCategories: yCats,
                minValue: values.length > 0 ? Math.min(...values) : 0,
                maxValue: values.length > 0 ? Math.max(...values) : 1,
                processedData: mapped
            };
        }

        // Fallback: Try to get field mappings from various sources
        const sciMappings = config.advanced?.scientific?.fieldMappings || config.scientificConfig?.fieldMappings || {};

        // Priority: Scientific mappings > Heatmap-specific config > X/Y axis config > Default
        const xField = sciMappings.xField
            || (config as any).heatmapColumn
            || (config as any).xCategoryField
            || (config as any).xAxis?.field
            || 'x';
        const yField = sciMappings.yField
            || (config as any).heatmapRow
            || (config as any).yCategoryField
            || (config as any).yAxis?.field
            || config.yField
            || 'y';
        const valField = sciMappings.colorField
            || (config as any).heatmapValue
            || (config as any).valueField
            || (config.measures?.[0]?.field)
            || 'value';

        console.log('[HeatmapRenderer] Field mappings:', { xField, yField, valField, sciMappings });
        console.log('[HeatmapRenderer] Config keys:', Object.keys(config));
        console.log('[HeatmapRenderer] Sample data:', data?.[0]);
        console.log('[HeatmapRenderer] Data length:', data?.length);

        // Map data using correct fields
        const mapped = data.map(d => {
            let val = d[valField];
            // Robust number parsing: handle strings like "70kg", "$100", etc.
            if (typeof val === 'string') {
                const match = val.match(/-?[\d,.]+/);
                if (match) val = parseFloat(match[0].replace(/,/g, ''));
            } else {
                val = Number(val);
            }

            return {
                x: d[xField],
                y: d[yField],
                value: val,
                original: d
            };
        }).filter(d => d.x != null && d.y != null && !isNaN(d.value));

        console.log('[HeatmapRenderer] Mapped data length:', mapped.length, 'sample:', mapped[0]);

        if (mapped.length === 0) {
            return {
                xCategories: [],
                yCategories: [],
                minValue: 0,
                maxValue: 1,
                processedData: [],
                error: `No valid data found. mapping: x=${xField}, y=${yField}, value=${valField}`
            };
        }

        const xCats = Array.from(new Set(mapped.map(d => d.x))).sort((a: any, b: any) => {
            // Try numeric sort if possible
            const an = Number(a), bn = Number(b);
            if (!isNaN(an) && !isNaN(bn)) return an - bn;
            return String(a).localeCompare(String(b));
        });
        const yCats = Array.from(new Set(mapped.map(d => d.y))).sort((a: any, b: any) => {
            const an = Number(a), bn = Number(b);
            if (!isNaN(an) && !isNaN(bn)) return an - bn;
            return String(a).localeCompare(String(b));
        });

        const values = mapped.map(d => d.value);
        return {
            xCategories: xCats,
            yCategories: yCats,
            minValue: values.length > 0 ? Math.min(...values) : 0,
            maxValue: values.length > 0 ? Math.max(...values) : 1,
            processedData: mapped
        };
    }, [data, config]);

    // Layout Constants - adjusted for better label display
    const LEGEND_WIDTH = 70; // Reduced legend width
    const LABEL_OFFSET = 15;
    const MARGIN = { top: 30, right: 10, bottom: 80, left: 120 }; // Adjusted margins

    // Calculate Dimensions - expand to fill available space
    const chartAreaWidth = width - LEGEND_WIDTH;
    const availableWidth = Math.max(0, chartAreaWidth - MARGIN.left - MARGIN.right);
    const availableHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);

    // Dynamic Cell Size - fill the entire available space
    const cellWidth = xCategories.length > 0 ? availableWidth / xCategories.length : 40;
    const cellHeight = yCategories.length > 0 ? availableHeight / yCategories.length : 40;

    // SVG exactly fits the container
    const svgWidth = chartAreaWidth;
    const svgHeight = height;

    const showLabels = config.customization?.showDataLabels !== false;

    // Axis formatting helper
    const getAxisLabel = (val: any, axis: 'x' | 'y') => {
        const advanced = config.advanced || {};
        const sciConfig = (config as any).scientificConfig || {}; // Fallback for direct prop
        const opts = (advanced as any).scientific?.axisOptions || sciConfig.axisOptions || {};

        const isLog = axis === 'x' ? opts.logScaleX : opts.logScaleY;
        const format = opts.axisFormat; // 'scientific' | 'engineering'

        if (typeof val === 'number' || !isNaN(Number(val))) {
            const num = Number(val);
            if (format === 'scientific' || isLog) return num.toExponential(1);
            // If log scale is on, scientific notation usually looks best for labels
        }
        return cleanLabel(String(val));
    };

    // Debug logging
    console.log('HeatmapRenderer:', {
        dataLength: processedData.length,
        xCats: xCategories.length,
        yCats: yCategories.length
    });

    if (width <= 0 || height <= 0) return null;

    // Display error if data mapping failed
    if ((processedData as any)?.error) {
        return (
            <div className="flex items-center justify-center w-full h-full text-red-400 text-xs font-mono p-4 text-center">
                {(processedData as any).error}
            </div>
        );
    }

    return (
        <div className="flex flex-row w-full h-full bg-transparent overflow-hidden" style={{ animation: 'fadeIn 300ms ease-out' }}>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
            {/* Chart Area - fills available space */}
            <div className="flex-1 h-full overflow-hidden relative">
                <svg width={svgWidth} height={svgHeight} className="w-full h-full">
                    <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
                        {/* Y Axis Labels */}
                        {yCategories.map((yCat, i) => {
                            const row = processedData.find(d => d.y === yCat && (d.image || d.original?.image));
                            const imageUrl = row?.image || row?.original?.image;
                            const label = getAxisLabel(yCat, 'y');
                            return (
                                <g key={i} transform={`translate(-10, ${i * cellHeight + cellHeight / 2})`}>
                                    {/* Image (Left of text) */}
                                    {imageUrl && (
                                        <image
                                            href={imageUrl}
                                            x={-45}
                                            y={-8}
                                            height={16}
                                            width={16}
                                        />
                                    )}
                                    <text
                                        x={imageUrl ? -25 : -5}
                                        y={4}
                                        textAnchor="end"
                                        fill="#d1d5db"
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                                        }}
                                    >
                                        {label.length > 15 ? label.slice(0, 12) + '...' : label}
                                    </text>
                                </g>
                            );
                        })}

                        {/* X Axis Labels */}
                        {xCategories.map((xCat, i) => {
                            const row = processedData.find(d => d.x === xCat && (d.image || d.original?.image));
                            const xImageUrl = row?.image || row?.original?.image;
                            const label = getAxisLabel(xCat, 'x');
                            return (
                                <g key={i} transform={`translate(${i * cellWidth + cellWidth / 2}, ${yCategories.length * cellHeight + 10})`}>
                                    <g transform="rotate(-45)">
                                        {/* Image (Beside text) */}
                                        {xImageUrl && (
                                            <image
                                                href={xImageUrl}
                                                x={-45}
                                                y={-8}
                                                height={16}
                                                width={16}
                                            />
                                        )}
                                        <text
                                            x={xImageUrl ? -25 : 0}
                                            y={5}
                                            textAnchor="end"
                                            fill="#d1d5db"
                                            style={{
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                                            }}
                                        >
                                            {label.length > 15 ? label.slice(0, 12) + '...' : label}
                                        </text>
                                    </g>
                                </g>
                            );
                        })}

                        {/* Cells */}
                        {processedData.map((d, i) => {
                            const xIndex = xCategories.indexOf(d.x);
                            const yIndex = yCategories.indexOf(d.y);
                            if (xIndex === -1 || yIndex === -1) return null;

                            const x = xIndex * cellWidth;
                            const y = yIndex * cellHeight;
                            const color = interpolateColor(d.value, minValue, maxValue, effectivePalette);
                            const isHovered = hoveredCell === d;

                            // Smart text color based on cell brightness
                            const brightness = d.value < (minValue + maxValue) / 2;

                            return (
                                <g
                                    key={i}
                                    transform={`translate(${x}, ${y})`}
                                    onMouseEnter={() => setHoveredCell(d)}
                                    onMouseLeave={() => setHoveredCell(null)}
                                >
                                    <rect
                                        width={Math.max(0.5, cellWidth - (cellWidth > 4 ? 1 : 0))}
                                        height={Math.max(0.5, cellHeight - (cellHeight > 4 ? 1 : 0))}
                                        fill={color}
                                        stroke={isHovered ? 'white' : 'none'}
                                        strokeWidth={isHovered ? 2 : 0}
                                        rx={cellWidth > 10 ? 2 : 0}
                                        className="transition-all duration-200"
                                        shapeRendering={cellWidth < 4 ? "crispEdges" : "auto"}
                                    />
                                    {showLabels && cellWidth > 30 && cellHeight > 20 && (
                                        <text
                                            x={cellWidth / 2}
                                            y={cellHeight / 2 + 4}
                                            textAnchor="middle"
                                            style={{
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                fill: brightness ? '#fbbf24' : '#1a1a2e',
                                                textShadow: brightness ? '0 1px 2px rgba(0,0,0,0.8)' : '0 1px 1px rgba(255,255,255,0.3)',
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            {typeof d.value === 'number' ? d.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : d.value}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                    </g>
                </svg>

                {/* Tooltip */}
                {hoveredCell && (
                    <div
                        className="absolute z-50 bg-slate-900 border border-slate-700 p-3 rounded-lg shadow-xl pointer-events-none"
                        style={{
                            left: MARGIN.left + xCategories.indexOf(hoveredCell.x) * cellWidth + cellWidth,
                            top: MARGIN.top + yCategories.indexOf(hoveredCell.y) * cellHeight
                        }}
                    >
                        <div className="text-xs font-bold text-white mb-1">
                            {cleanLabel(String(hoveredCell.x))} vs {cleanLabel(String(hoveredCell.y))}
                        </div>
                        <div className="text-sm text-blue-400 font-mono">
                            Value: {hoveredCell.value?.toLocaleString()}
                        </div>
                    </div>
                )}
            </div>

            {/* Right Side Legend - only show when data exists */}
            {processedData.length > 0 && (
                <div
                    className="flex-shrink-0 flex flex-col items-center justify-center border-l border-gold-500/20 bg-obsidian-900/50"
                    style={{ width: LEGEND_WIDTH, padding: '16px 8px' }}
                >
                    <div className="text-[10px] text-gray-300 mb-2 font-medium">
                        {typeof maxValue === 'number' ? maxValue.toLocaleString(undefined, { maximumFractionDigits: 1 }) : maxValue}
                    </div>
                    <svg width={16} height={Math.max(100, height - 100)} className="flex-1">
                        <defs>
                            <linearGradient id="heatmapLegendGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                                {effectivePalette.map((color, i) => (
                                    <stop
                                        key={i}
                                        offset={`${(i / (effectivePalette.length - 1)) * 100}%`}
                                        stopColor={color}
                                    />
                                ))}
                            </linearGradient>
                        </defs>
                        <rect
                            width={16}
                            height="100%"
                            fill="url(#heatmapLegendGradient)"
                            rx={3}
                            style={{ filter: 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.2))' }}
                        />
                    </svg>
                    <div className="text-[10px] text-gray-300 mt-2 font-medium">
                        {typeof minValue === 'number' ? minValue.toLocaleString(undefined, { maximumFractionDigits: 1 }) : minValue}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-3 text-center">
                        {config.valueField || 'Value'}
                    </div>
                </div>
            )}
        </div>
    );
};
